import React from "react";
import FeatureBanner from "../../shared/ui/FeatureBanner";
import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  BoltIcon,
  CircleStackIcon,
  ClockIcon,
  CpuChipIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

const GUIDE_STEPS = [
  {
    title: "Choose product and environment",
    body: "Select the product, then Development or Production.",
  },
  {
    title: "Open a feature",
    body: "Choose Worker Scaling, Migration, or another task.",
  },
  {
    title: "Review and apply",
    body: "Check values, apply the change, and monitor status.",
  },
];

const SAFETY_NOTES = [
  {
    title: "Production is live",
    body: "Changes affect live services.",
    icon: ShieldCheckIcon,
    tone: "border-red-200 bg-red-50 text-red-700",
  },
  {
    title: "Development is isolated",
    body: "Changes do not affect live users.",
    icon: WrenchScrewdriverIcon,
    tone: "border-green-200 bg-green-50 text-green-700",
  },
];

const FEATURE_SUMMARY = [
  {
    title: "Power On/Off",
    body: "Start or stop resources.",
    icon: BoltIcon,
  },
  {
    title: "Worker Scaling",
    body: "Manage worker capacity and size.",
    icon: ServerStackIcon,
  },
  {
    title: "DB Elastic Pool",
    body: "Adjust SQL pool capacity.",
    icon: CircleStackIcon,
  },
  {
    title: "Worker Maintenance",
    body: "Clean up worker images.",
    icon: CpuChipIcon,
  },
  {
    title: "DB Backup Schedule / Retention",
    body: "Set backup timing and retention.",
    icon: ClockIcon,
  },
  {
    title: "DB Server Scaling",
    body: "Resize MySQL server compute.",
    icon: AdjustmentsHorizontalIcon,
  },
  {
    title: "Blob Storage Archive",
    body: "Back up school containers to ZIP.",
    icon: ArchiveBoxIcon,
  },
  {
    title: "Migration",
    body: "Create and track GradeXpert migrations.",
    icon: ArrowPathIcon,
  },
];

const panelClassName = "workspace-card overflow-hidden border border-slate-200 bg-white p-4";
const innerCardClassName = "workspace-subcard border border-slate-200";

const HomePage = () => {
  return (
    <div className="mx-auto min-h-full w-full max-w-[1440px] min-w-0 overflow-auto space-y-2">
      <FeatureBanner className="workspace-card px-5 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-5">Infrastructure Portal</h1>
            <p className="mt-1 max-w-2xl text-xs leading-5 text-blue-50">
              Manage Accelerus, Xuno, BPOS, and GradeXpert infrastructure.
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-white/40 bg-white/10 px-2 py-1 text-[11px] font-semibold leading-4 text-white">
            Utilities 3.0
          </span>
        </div>
      </FeatureBanner>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <section className={panelClassName}>
          <div className="border-b border-slate-200 pb-3">
            <h2 className="text-sm font-semibold leading-5 text-slate-800">How to use</h2>
            <p className="mt-1 text-xs leading-4 text-slate-500">A simple flow for every change.</p>
          </div>
          <ol className="mt-3 space-y-2">
            {GUIDE_STEPS.map((step, index) => (
              <li key={step.title} className="flex gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1976d2] text-[10px] font-semibold text-white">
                  {index + 1}
                </span>
                <div>
                  <p className="text-xs font-semibold leading-4 text-slate-700">{step.title}</p>
                  <p className="mt-0.5 text-[11px] leading-4 text-slate-500">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className={panelClassName}>
          <div className="border-b border-slate-200 pb-3">
            <h2 className="text-sm font-semibold leading-5 text-slate-800">Important notes</h2>
            <p className="mt-1 text-xs leading-4 text-slate-500">Verify the environment before applying.</p>
          </div>
          <div className="mt-3 space-y-2">
            {SAFETY_NOTES.map((note) => (
              <div key={note.title} className={`${innerCardClassName} p-2.5 ${note.tone}`}>
                <div className="flex items-center gap-2">
                  <note.icon className="h-4 w-4 shrink-0" />
                  <p className="text-xs font-semibold leading-4">{note.title}</p>
                </div>
                <p className="mt-1 text-[11px] leading-4">{note.body}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={panelClassName}>
        <div className="border-b border-slate-200 pb-3">
          <h2 className="text-sm font-semibold leading-5 text-slate-800">Feature summary</h2>
          <p className="mt-1 text-xs leading-4 text-slate-500">Choose the page that matches your task.</p>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURE_SUMMARY.map((item) => (
            <div key={item.title} className={`${innerCardClassName} flex items-start gap-2 p-2.5`}>
              <item.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1976d2]" />
              <span className="min-w-0">
                <span className="block text-xs font-semibold leading-4 text-slate-800">{item.title}</span>
                <span className="mt-0.5 block text-[11px] leading-4 text-slate-500">{item.body}</span>
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
